<template>
  <a-card title="帖子模板配置">
    <a-space>
      <a-form layout="inline" :model="formSearch" style="margin: 10px auto;">
        <a-form-item>
          <a-input placeholder="模板帖子 ID" v-model:value="formSearch.id" />
        </a-form-item>
        <a-form-item>
          <a-input placeholder="发布者 UID" v-model:value="formSearch.user_id" />
        </a-form-item>
        <a-form-item label="工具">
          <a-select
            :allowClear="true"
            style="width: 140px;"
            v-model:value="formSearch.tool_id"
            :options="ToolIDOptions"
            placeholder="工具"
          />
        </a-form-item>
        <a-form-item label="状态">
          <a-select
            :allowClear="true"
            style="width: 120px;"
            v-model:value="formSearch.is_delete"
            :options="statusOptions"
            placeholder="状态"
          />
        </a-form-item>
        <a-form-item label="是否海外">
          <a-select
            :allowClear="true"
            style="width: 120px;"
            v-model:value="formSearch.is_oversea"
            :options="overseaOptions"
            placeholder="是否海外"
          />
        </a-form-item>
        <a-form-item label="排序">
          <a-select
            :allowClear="true"
            style="width: 160px;"
            v-model:value="formSearch.orderby"
            :options="orderbyOptions"
            placeholder="排序"
          />
        </a-form-item>
        <a-form-item>
          <a-button
            type="primary"
            html-type="submit"
            @click="handleTableChange()"
            shape="circle"
          >
            <template #icon><SearchOutlined /></template>
          </a-button>
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="handleCreate">新建</a-button>
        </a-form-item>
      </a-form>
    </a-space>

    <a-alert :message="noticeMessage" type="info" show-icon style="text-align: left;" />

    <a-table
      class="ant-table-striped"
      :row-key="record => record.id"
      :data-source="dataSource"
      :columns="columns"
      :pagination="pagination"
      :loading="loading"
      @change="handleTableChange"
      :row-class-name="(_record, index) => (index % 2 === 1 ? 'table-striped' : null)"
    >
      <template #tool_id="{ record }">
        {{ ToolNames[record.tool_id] || record.tool_id || '-' }}
      </template>
      <template #template_id="{ record }">
        <div class="template-name">{{ record.template_name || '-' }}</div>
        <div class="muted-id">{{ record.template_id || '-' }}</div>
      </template>
      <template #template_desc="{ record }">
        <div>{{ record.title || '-' }}</div>
        <div class="form-tip">{{ record.title_key || '-' }}</div>
      </template>
      <template #cover_url="{ record }">
        <template v-if="record.cover_url_type == 2">
          <video
            v-if="record.cover_url"
            :src="rawImage(record.cover_url)"
            style="max-width: 160px; max-height: 120px;"
            controls
          />
        </template>
        <a-image
          v-else-if="record.cover_url"
          style="width: 120px"
          :preview="{ src: rawImage(record.cover_url) }"
          :src="thumbnail(record.cover_url)"
        />
      </template>
      <template #publisher="{ record }">
        <div class="publisher-preview">
          <a-avatar :src="record.avatar || record.publisher_avatar" :size="32">
            {{ record.nickname ? record.nickname.slice(0, 1) : 'U' }}
          </a-avatar>
          <span>{{ record.nickname ? `@${record.nickname}` : (record.user_id ? `@${record.user_id}` : '-') }}</span>
        </div>
      </template>
      <template #is_oversea="{ record }">
        <a-tag v-if="record.is_oversea == 1" color="purple">海外</a-tag>
        <a-tag v-else color="blue">国内</a-tag>
      </template>
      <template #is_delete="{ record }">
        <a-tag v-if="record.is_delete == 1" color="red">已删除</a-tag>
        <a-tag v-else color="green">正常</a-tag>
      </template>
      <template #created_at="{ record }">
        <div>创建时间: {{ record.created_at ? record.created_at.toLocaleString() : '-' }}</div>
        <div>修改时间: {{ record.updated_at ? record.updated_at.toLocaleString() : '-' }}</div>
      </template>
      <template #action="{ record }">
        <a-space>
          <a-button @click="handleEdit(record)">编辑</a-button>
          <a-popconfirm
            title="确定删除吗?"
            ok-text="确定"
            cancel-text="不了"
            @confirm="handleDelete(record)"
          >
            <a-button danger>删除</a-button>
          </a-popconfirm>
        </a-space>
      </template>
    </a-table>
  </a-card>

  <a-modal
    v-model:open="openDia"
    :title="isEdit ? '编辑模板帖子' : '新建模板帖子'"
    @ok="handleConfirm"
    @cancel="resetForm"
    :confirm-loading="saving"
    width="980px"
    destroy-on-close
  >
    <a-form :label-col="{ span: 6 }" :model="formRecord">
      <a-form-item label="模板 ID" required>
        <a-input
          v-model:value="formRecord.template_id"
          placeholder="模板 ID"
          :disabled="isEdit"
          @change="() => debouncedFetchTemplateInfo(formRecord.template_id)"
        />
      </a-form-item>

      <div class="template-info-container">
        <div v-if="templateLoading" class="template-loading">
          <a-spin size="small" />
          <span>正在获取模板信息...</span>
        </div>
        <a-alert v-else-if="templateError" type="error" :message="templateError" show-icon />
        <div v-else-if="templateInfo" class="template-info">
          <div class="template-info-left">
            <a-image v-if="templateInfo.image_url" :src="templateInfo.image_url" style="width: 120px;" />
          </div>
          <div class="template-info-right">
            <div class="template-info-item">
              <span class="template-info-label">后台标题:</span>
              <span>{{ templateInfo.title || '-' }}</span>
            </div>
            <div class="template-info-item">
              <span class="template-info-label">工具:</span>
              <span>{{ ToolNames[templateInfo.tool_id] || templateInfo.tool_id || '-' }}</span>
            </div>
            <div class="template-info-item">
              <span class="template-info-label">任务类型:</span>
              <span>{{ StyleFlowNames[templateInfo.style_id] || templateInfo.style_id || '-' }}</span>
            </div>
          </div>
        </div>
      </div>

      <a-alert
        v-if="templateInfo && !isAllowedStyle"
        type="error"
        show-icon
        message="该模板的任务类型不支持配置到社区"
        style="margin-bottom: 12px;"
      />
      <a-alert
        v-if="templateInfo && isSpreadable"
        type="error"
        show-icon
        message="该模板需要手动涂抹，社区暂不支持"
        style="margin-bottom: 12px;"
      />

      <a-divider>社区展示配置</a-divider>
      <a-form-item label="工具" required>
        <a-select
          v-model:value="formRecord.tool_id"
          :options="ToolIDOptions"
          placeholder="工具"
          :disabled="true"
        />
      </a-form-item>
      <a-form-item label="模板标签">
        <a-descriptions size="small" bordered :column="1">
          <a-descriptions-item label="中文">{{ templateLabelZh || '-' }}</a-descriptions-item>
          <a-descriptions-item label="英文">{{ templateLabelEn || '-' }}</a-descriptions-item>
        </a-descriptions>
      </a-form-item>
      <a-form-item label="帖子封面视频" required>
        <UploadVideo v-model:videoURL="formRecord.cover_url" />
        <a-input placeholder="视频 URL" v-model:value="formRecord.cover_url" />
        <div class="form-tip">分辨率不超过 1920*1080；文件大小不超过 50MB；时长不超过 20s</div>
      </a-form-item>
      <a-form-item label="发布者 UID" required>
        <a-input placeholder="uid" v-model:value="formRecord.user_id" style="width: 200px;" :disabled="isEdit" />
        <a-button style="margin-left: 8px;" @click="fetchPublisherInfo">查询</a-button>
        <span v-if="publisherError" class="error-text">{{ publisherError }}</span>
      </a-form-item>
      <a-form-item label="发布者预览">
        <div class="publisher-preview">
          <a-avatar :src="publisherInfo.avatar" :size="48">
            {{ publisherInfo.nickname ? publisherInfo.nickname.slice(0, 1) : 'U' }}
          </a-avatar>
          <div>
            <div>{{ publisherDisplayName }}</div>
            <div class="form-tip">头像与用户名来自 UID</div>
          </div>
        </div>
      </a-form-item>
      <a-form-item label="是否海外">
        <a-select
          v-model:value="formRecord.is_oversea"
          style="width: 120px;"
          :options="overseaOptions"
          :disabled="isEdit"
        />
      </a-form-item>
      <a-form-item label="排序">
        <a-input-number v-model:value="formRecord.sort_order" style="width: 120px;" />
      </a-form-item>

      <a-form-item label="正文描述(国际化)" required>
        <Inputi18nInline
          showTitle="true"
          v-model:valid="formRecord.title_valid"
          v-model:value="formRecord.title_key"
        />
        <div class="form-tip">{{ formRecord.title_key || '-' }}</div>
      </a-form-item>

      <a-divider>任务类型兼容说明</a-divider>
      <a-alert
        type="info"
        show-icon
        message="算法的识别不会中断，只有当作参数传入算法的前置识别才会中断。"
        style="margin-bottom: 12px;"
      />
      <a-table
        size="small"
        :pagination="false"
        :data-source="compatibilityRows"
        :columns="compatibilityColumns"
        row-key="style_id"
        class="compatibility-table"
      />
    </a-form>
  </a-modal>
</template>

<script>
import { defineComponent } from 'vue'
import { SearchOutlined } from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { debounce } from 'lodash'
import { useStore } from 'vuex'
import request from '@/utils/request'
import { thumbnail, rawImage } from '@/utils/image'
import { ToolNames, ToolIDOptions, StyleFlowNames } from '@/utils/macro'
import { generateRandomString } from '@/utils/string'
import UploadVideo from '@/components/UploadVideo.vue'
import Inputi18nInline from '@/components/Inputi18nInline.vue'

const API_LIST = '/api/community/template_articles'
const API_CREATE = '/api/community/article/create_template'
const API_DELETE = '/api/community/articles'
const API_UPDATE = '/api/community/articles'
const ALLOWED_STYLE_IDS = new Set([
  '0',
  '32',
  '36',
  '44',
  '30',
  '41',
  '13',
  '22',
  '53',
  '38',
  '29',
  '47',
  '33',
  '51', // 美体(Seedream)
  '52', // 美体(Lora)
  '57', // 美颜(工作流seedream)
  '55', // 美手(qwen)
  '69', // P视频
  '70', // 视频增加
  '71', // 视频替换
  '72', // 视频消除
  '73', // 视频换装
  '74', // 视频背景
])

const DefaultRecord = {
  id: 0,
  user_id: '',
  tool_id: '',
  template_id: '',
  title_key: '',
  cover_url: '',
  is_oversea: 0,
  sort_order: 0,
  title_valid: false,
}

const compatibilityRows = [
  {
    style_id: '0',
    name: 'Omni(聚合)',
    interrupt: '/',
    remark: '/',
    priority: 'P0'
  },
  {
    style_id: '32',
    name: 'Seedream 4.0',
    interrupt: '/',
    remark: '/',
    priority: 'P0'
  },
  {
    style_id: '36',
    name: '海外 Seedream 4.0',
    interrupt: '/',
    remark: '/',
    priority: 'P0'
  },
  {
    style_id: '44',
    name: 'Seedream 4.5',
    interrupt: '/',
    remark: '/',
    priority: 'P0'
  },
  {
    style_id: '30',
    name: 'nano-banana',
    interrupt: '/',
    remark: '/',
    priority: 'P0'
  },
  {
    style_id: '41',
    name: 'nano-banana-pro(thrid)',
    interrupt: '/',
    remark: '/',
    priority: 'P0'
  },
  {
    style_id: '13',
    name: '换装',
    interrupt: '✅',
    remark: '组合"服装识别"和"换装"工作流，未识别到服装将终止',
    priority: 'P1'
  },
  {
    style_id: '22',
    name: '换脸',
    interrupt: '✅',
    remark: '未识别到人脸 / 人脸数量 >1 将终止',
    priority: 'P0'
  },
  {
    style_id: '53',
    name: '视频换脸',
    interrupt: '✅',
    remark: '/',
    priority: 'P0'
  },
  {
    style_id: '38',
    name: '换背景(参考图 qwen)',
    interrupt: '✅',
    remark: '组合"主体识别"和"换背景"，识别结果不影响生成',
    priority: 'P1'
  },
  {
    style_id: '29',
    name: 'kontext-dev',
    interrupt: '✅',
    remark: '组合"主体识别"和"换背景"，识别结果不影响生成',
    priority: 'P1'
  },
  {
    style_id: '47',
    name: '换背景(参考图fluxpro)',
    interrupt: '✅',
    remark: '组合"主体识别"和"换背景"，识别结果不影响生成',
    priority: 'P1'
  },
  {
    style_id: '33',
    name: '美甲(banana)',
    interrupt: '/',
    remark: '未识别到手将终止',
    priority: 'P1'
  },
    {
    style_id: '69',
    name: 'P视频',
    interrupt: '/',
    remark: '',
    priority: 'P1'
  },
    {
    style_id: '70',
    name: '视频增加',
    interrupt: '/',
    remark: '',
    priority: 'P1'
  },
    {
    style_id: '71',
    name: '视频替换',
    interrupt: '/',
    remark: '',
    priority: 'P1'
  },
    {
    style_id: '72',
    name: '视频消除',
    interrupt: '/',
    remark: '',
    priority: 'P1'
  },
    {
    style_id: '73',
    name: '视频换装',
    interrupt: '/',
    remark: '',
    priority: 'P1'
  },
    {
    style_id: '74',
    name: '视频背景',
    interrupt: '/',
    remark: '',
    priority: 'P1'
  }
]

export default defineComponent({
  components: {
    SearchOutlined,
    UploadVideo,
    Inputi18nInline,
  },
  data() {
    const store = useStore()
    return {
      ToolNames,
      ToolIDOptions,
      StyleFlowNames,
      operator_id: store.state.user.user_id,
      loading: false,
      saving: false,
      openDia: false,
      isEdit: false,
      dataSource: [],
      pagination: {
        total: 1,
        current: 1,
        pageSize: 20,
        'show-size-changer': true,
        pageSizeOptions: ['20', '50', '100']
      },
      noticeMessage: '',
      formSearch: {
        id: '',
        user_id: '',
        tool_id: '',
        is_delete: '',
        is_oversea: '',
        orderby: 'created_at desc'
      },
      statusOptions: [
        { label: '正常', value: '0' },
        { label: '删除', value: '1' },
        { label: '所有', value: '' }
      ],
      orderbyOptions: [
        { label: '发布时间倒序', value: 'created_at desc' },
        { label: '推荐顺序倒序', value: 'sort_order desc' }
      ],
      overseaOptions: [
        { label: '全部', value: '' },
        { label: '国内', value: 0 },
        { label: '海外', value: 1 }
      ],
      formRecord: {
        ...DefaultRecord,
      },
      templateInfo: null,
      templateLoading: false,
      templateError: '',
      templateNameI18n: {
        lang_zh: '',
        lang_en: '',
      },
      publisherInfo: {
        avatar: '',
        nickname: '',
      },
      publisherError: '',
      debouncedFetchTemplateInfo: null,
      compatibilityRows,
      compatibilityColumns: [
        { title: 'style_id', dataIndex: 'style_id', key: 'style_id', width: 80 },
        { title: '任务类型', dataIndex: 'name', key: 'name' },
        { title: '识别是否中断', dataIndex: 'interrupt', key: 'interrupt', width: 120 },
        { title: '备注', dataIndex: 'remark', key: 'remark' },
        { title: '优先级', dataIndex: 'priority', key: 'priority', width: 80 },
      ],
    }
  },
  computed: {
    templateLabelZh() {
      if (!this.templateInfo) {
        return ''
      }
      const toolName = this.ToolNames[this.templateInfo.tool_id] || this.templateInfo.tool_id || ''
      const nameZh = this.templateNameI18n.lang_zh || this.templateInfo.name_zh || ''
      return `${toolName} ${nameZh}`.trim()
    },
    templateLabelEn() {
      if (!this.templateInfo) {
        return ''
      }
      const toolName = this.templateInfo.tool_id || ''
      const nameEn = this.templateNameI18n.lang_en || ''
      return `${toolName} ${nameEn}`.trim()
    },
    publisherDisplayName() {
      const nickname = this.publisherInfo.nickname || ''
      if (!nickname) {
        return this.formRecord.user_id ? `@${this.formRecord.user_id}` : '-'
      }
      return `@${nickname}`
    },
    isSpreadable() {
      if (!this.templateInfo) {
        return false
      }
      return `${this.templateInfo.spreadable}` === '1'
    },
    isAllowedStyle() {
      if (!this.templateInfo) {
        return true
      }
      return ALLOWED_STYLE_IDS.has(`${this.templateInfo.style_id}`)
    }
  },
  mounted() {
    this.debouncedFetchTemplateInfo = debounce(this.fetchTemplateInfo, 500)
    this.handleTableChange()
  },
  methods: {
    thumbnail,
    rawImage,
    resetForm() {
      this.openDia = false
      this.isEdit = false
      this.formRecord = { ...DefaultRecord }
      this.templateInfo = null
      this.templateError = ''
      this.publisherInfo = { avatar: '', nickname: '' }
      this.publisherError = ''
      this.templateNameI18n = { lang_zh: '', lang_en: '' }
    },
    handleCreate() {
      this.openDia = true
      this.isEdit = false
      const keySeed = generateRandomString()
      this.formRecord = {
        ...DefaultRecord,
        title_key: `community.template.desc.${keySeed}`,
        title_valid: false
      }
      this.templateInfo = null
      this.templateError = ''
      this.publisherInfo = { avatar: '', nickname: '' }
      this.publisherError = ''
      this.templateNameI18n = { lang_zh: '', lang_en: '' }
    },
    handleEdit(record) {
      this.openDia = true
      this.isEdit = true
      this.formRecord = {
        ...DefaultRecord,
        id: record.id,
        user_id: record.user_id ? `${record.user_id}` : '',
        tool_id: record.tool_id || '',
        template_id: record.template_id ? `${record.template_id}` : '',
        title_key: record.title_key || '',
        cover_url: record.cover_url ? rawImage(record.cover_url) : '',
        is_oversea: record.is_oversea || 0,
        sort_order: record.sort_order || 0,
        title_valid: !!record.title_key,
      }
      if (this.formRecord.template_id) {
        this.fetchTemplateInfo(this.formRecord.template_id)
      }
      if (this.formRecord.user_id) {
        this.fetchPublisherInfo()
      }
    },
    async handleDelete(record) {
      this.loading = true
      try {
        await request.delete(`${API_DELETE}/${record.id}`)
        this.handleTableChange()
      } finally {
        this.loading = false
      }
    },
    async ensureTitleI18n() {
      if (!this.formRecord.title_key) {
        return false
      }
      return true
    },
    async handleConfirm() {
      if (!this.formRecord.user_id) {
        message.error('请填写发布者 UID')
        return
      }
      if (!this.formRecord.template_id) {
        message.error('请填写模板 ID')
        return
      }
      if (!this.formRecord.tool_id) {
        message.error('请先选择模板获取工具信息')
        return
      }
      if (!this.formRecord.cover_url) {
        message.error('请上传帖子封面视频')
        return
      }
      if (!this.isAllowedStyle) {
        message.error('该任务类型不支持配置到社区')
        return
      }
      if (this.isSpreadable) {
        message.error('该模板需要手动涂抹，无法配置到社区')
        return
      }
      let ok = true
      if (!this.isEdit ) {
        ok = await this.ensureTitleI18n()
      }
      if (!ok) {
        return
      }
      if (!this.formRecord.title_valid) {
        message.error('请先保存正文描述的多语言文案')
        return
      }

      const payload = {
        user_id: +this.formRecord.user_id,
        operator: this.operator_id,
        tool_id: this.formRecord.tool_id,
        template_id: +this.formRecord.template_id,
        title_key: this.formRecord.title_key,
        cover_url: this.formRecord.cover_url,
        cover_url_type: 2,
        is_oversea: +this.formRecord.is_oversea,
        sort_order: +this.formRecord.sort_order,
      }

      this.saving = true
      try {
        if (this.isEdit) {
          await request.post(`${API_UPDATE}/${this.formRecord.id}/update`, {
            title_key: payload.title_key,
            cover_url: payload.cover_url,
            sort_order: payload.sort_order,
          })
        } else {
          await request.post(API_CREATE, payload)
        }
        this.resetForm()
        this.handleTableChange()
      } catch (error) {
        message.error(this.isEdit ? '更新失败' : '创建失败')
      } finally {
        this.saving = false
      }
    },
    async fetchTemplateInfo(templateId) {
      const id = +templateId
      if (!id) {
        this.templateInfo = null
        this.templateError = ''
        this.templateNameI18n = { lang_zh: '', lang_en: '' }
        return
      }
      this.templateLoading = true
      this.templateError = ''
      try {
        const rsp = await request.get('/api/creation/options', {
          template_id: id,
          status: 1,
          limit: 1
        })
        if (rsp.data.list && rsp.data.list[0]) {
          this.templateInfo = rsp.data.list[0]
          this.formRecord.tool_id = this.templateInfo.tool_id || ''
          await this.fetchTemplateI18n(this.templateInfo.name)
        } else {
          this.templateInfo = null
          this.templateError = '未找到该模板信息'
        }
      } catch (error) {
        this.templateInfo = null
        this.templateError = '获取模板信息失败，请稍后重试'
      } finally {
        this.templateLoading = false
      }
    },
    async fetchTemplateI18n(key) {
      if (!key) {
        this.templateNameI18n = { lang_zh: '', lang_en: '' }
        return
      }
      try {
        const rsp = await request.get('/api/i18n/texts/key', { key })
        if (rsp.data.result) {
          this.templateNameI18n = rsp.data.result
        } else {
          this.templateNameI18n = { lang_zh: '', lang_en: '' }
        }
      } catch (error) {
        this.templateNameI18n = { lang_zh: '', lang_en: '' }
      }
    },
    async fetchPublisherInfo() {
      if (!this.formRecord.user_id) {
        this.publisherInfo = { avatar: '', nickname: '' }
        this.publisherError = ''
        return
      }
      this.publisherError = ''
      try {
        const rsp = await request.get('/api/users/', {
          user_id: this.formRecord.user_id,
          offset: 0,
          limit: 1,
        })
        const user = rsp.data.list && rsp.data.list[0]
        if (user) {
          this.publisherInfo = {
            avatar: user.avatar || '',
            nickname: user.nickname || '',
          }
        } else {
          this.publisherInfo = { avatar: '', nickname: '' }
          this.publisherError = '未找到用户信息'
        }
      } catch (error) {
        this.publisherInfo = { avatar: '', nickname: '' }
        this.publisherError = '获取用户信息失败'
      }
    },
    async handleTableChange(pagi = { pageSize: 20, current: 1 }) {
      this.loading = true
      try {
        const query = {
          offset: (pagi.current - 1) * pagi.pageSize,
          limit: pagi.pageSize,
          id: this.formSearch.id,
          user_id: this.formSearch.user_id,
          tool_id: this.formSearch.tool_id,
          orderby: this.formSearch.orderby,
        }
        if (this.formSearch.is_delete !== '') {
          query.is_delete = +this.formSearch.is_delete
        }
        if (this.formSearch.is_oversea !== '') {
          query.is_oversea = +this.formSearch.is_oversea
        }
        const rsp = await request.get(API_LIST, query)
        // 确保数据安全处理
        if (rsp && rsp.data) {
          this.dataSource = [].concat(rsp.data.list || [])
          for (let item of this.dataSource) {
            if (item.created_at) {
              item.created_at = new Date(item.created_at)
            }
            if (item.updated_at) {
              item.updated_at = new Date(item.updated_at)
            }
          }
          if (rsp.data.pagination) {
            this.noticeMessage = `共 ${rsp.data.pagination.total || 0} 条`
            this.pagination.total = rsp.data.pagination.total || 0
            this.pagination.current = pagi.current
            this.pagination.pageSize = pagi.pageSize
          } else {
            this.noticeMessage = `共 ${this.dataSource.length} 条`
            this.pagination.total = this.dataSource.length
            this.pagination.current = pagi.current
            this.pagination.pageSize = pagi.pageSize
          }
        } else {
          // 当返回数据为空时，展示空页面
          this.dataSource = []
          this.noticeMessage = '共 0 条'
          this.pagination.total = 0
          this.pagination.current = pagi.current
          this.pagination.pageSize = pagi.pageSize
        }
      } catch (error) {
        console.error('获取数据失败:', error)
        // 当发生错误时，展示空页面而不是报错
        this.dataSource = []
        this.noticeMessage = '共 0 条'
        this.pagination.total = 0
        this.pagination.current = pagi.current
        this.pagination.pageSize = pagi.pageSize
      } finally {
        this.loading = false
      }
    },
  },
  setup() {
    return {
      columns: [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
        { title: '发布者 UID', dataIndex: 'user_id', key: 'user_id', width: 100 },
        { title: '发布者', dataIndex: 'publisher', key: 'publisher', slots: { customRender: 'publisher' } },
        { title: '工具', dataIndex: 'tool_id', key: 'tool_id', slots: { customRender: 'tool_id' } },
        { title: '模板 ID', dataIndex: 'template_id', key: 'template_id', width: 140, slots: { customRender: 'template_id' } },
        { title: '正文描述', dataIndex: 'title', key: 'title', slots: { customRender: 'template_desc' } },
        { title: '封面', dataIndex: 'cover_url', key: 'cover_url', slots: { customRender: 'cover_url' } },
        { title: '排序', dataIndex: 'sort_order', key: 'sort_order', width: 80 },
        { title: '海外', dataIndex: 'is_oversea', key: 'is_oversea', slots: { customRender: 'is_oversea' } },
        { title: '状态', dataIndex: 'is_delete', key: 'is_delete', slots: { customRender: 'is_delete' } },
        { title: '时间', dataIndex: 'created_at', key: 'created_at', slots: { customRender: 'created_at' } },
        { title: '操作', dataIndex: 'action', key: 'action', slots: { customRender: 'action' } },
      ],
    }
  }
})
</script>

<style scoped>
.ant-table-striped :deep(.table-striped) td {
  background-color: #fafafa;
}

.template-info-container {
  margin: 8px 0 16px;
}

.template-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
}

.template-info {
  display: flex;
  gap: 16px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
  border-left: 4px solid #1890ff;
}

.template-info-left {
  flex: 0 0 auto;
}

.template-info-right {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.template-info-item {
  font-size: 13px;
}

.template-info-label {
  color: #666;
  margin-right: 8px;
}

.publisher-preview {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-tip {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.muted-id {
  font-size: 12px;
  color: #666;
}

.template-name {
  font-weight: 600;
  color: #333;
}

.error-text {
  margin-left: 8px;
  color: #ff4d4f;
}

.compatibility-table {
  margin-bottom: 16px;
}
</style>
